import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';

const TransformationSection = () => {
  const { t } = useTranslation();

  const comparisonPoints = [
    {
      before: {
        title: 'Papiergestützte Auftragserfassung',
        description: 'Handschriftliche Notizen auf Papierformularen, Bestellungen müssen nachträglich im Büro erfasst werden.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      },
      after: {
        title: 'Digitale Erfassung in Echtzeit',
        description: 'Bestellungen werden direkt digital erfasst und sofort ins ERP-System übertragen.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        )
      }
    },
    {
      before: {
        title: 'Keine aktuellen Daten unterwegs',
        description: 'Verkäufer haben keinen Zugriff auf aktuelle Preise, Bestände oder Kundenhistorie beim Kundentermin.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      after: {
        title: 'Alle Informationen in Echtzeit',
        description: 'Vollständiger Zugriff auf Lagerbestände, Preise, frühere Bestellungen und individuelle Rabatte.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        )
      }
    },
    {
      before: {
        title: 'Lange Auftragsbearbeitungszeiten',
        description: 'Bis zu 3 Tage Verzögerung zwischen Bestellaufnahme und tatsächlicher Auftragserfassung im System.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      after: {
        title: 'Sofortige Auftragsbearbeitung',
        description: 'Bestellungen werden unmittelbar im System verarbeitet, Kommissionierung kann sofort beginnen.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        )
      }
    },
    {
      before: {
        title: 'Fehleranfällige Übertragung',
        description: 'Durchschnittlich 8-12% Übertragungsfehler durch manuelle Dateneingabe.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      },
      after: {
        title: 'Nahezu fehlerfreie Datenerfassung',
        description: 'Fehlerquote unter 1% durch direkte digitale Auftragserfassung.',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      }
    }
  ];

  return (
    <section className="py-20 bg-blue-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-blue-900">
            Vorher vs. Nachher: Die Transformation deines Außendienstes
          </h2>
          <p className="text-xl text-gray-700">
            Erlebe den Unterschied zwischen traditioneller Auftragserfassung und der Effizienz mit Salescockpit.
          </p>
        </div>
        
        <div className="space-y-12">
          {comparisonPoints.map((point, index) => (
            <div key={index} className="flex flex-col md:flex-row">
              {/* Vorher */}
              <div className="flex-1 bg-white p-6 rounded-tl-xl rounded-tr-xl md:rounded-tr-none md:rounded-bl-xl shadow-md mb-2 md:mb-0 md:mr-2">
                <div className="flex items-start mb-4">
                  {point.before.icon}
                  <h3 className="text-xl font-semibold ml-3 text-gray-900">{point.before.title}</h3>
                </div>
                <p className="text-gray-700">{point.before.description}</p>
                <div className="mt-4 py-1 px-3 bg-red-100 text-red-700 text-sm font-medium rounded-full inline-block">
                  VORHER
                </div>
              </div>
              
              {/* Verbindungselement nur für Desktop */}
              <div className="hidden md:flex items-center justify-center w-16">
                <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </div>
              
              {/* Nachher */}
              <div className="flex-1 bg-white p-6 rounded-bl-xl rounded-br-xl md:rounded-bl-none md:rounded-tr-xl md:rounded-br-xl shadow-md md:ml-2">
                <div className="flex items-start mb-4">
                  {point.after.icon}
                  <h3 className="text-xl font-semibold ml-3 text-gray-900">{point.after.title}</h3>
                </div>
                <p className="text-gray-700">{point.after.description}</p>
                <div className="mt-4 py-1 px-3 bg-green-100 text-green-700 text-sm font-medium rounded-full inline-block">
                  NACHHER
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="max-w-3xl mx-auto text-center mt-16">
          <p className="text-xl font-semibold text-blue-700">
            Die Digitalisierung deines Außendienstes mit Salescockpit zahlt sich vom ersten Tag an aus.
          </p>
          <div className="mt-8">
            <span className="inline-block bg-blue-800 text-white rounded-full px-5 py-3 font-semibold">
              Zeit- und Kostenersparnis von durchschnittlich 63% pro Außendienstmitarbeiter
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformationSection; 